import { ChangeDetectionStrategy, Component, computed, ContentChild, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonGroupModule, CheckboxComponent } from '@libs/ui-lib';
import { PushTagOnClickDirective, TagEvent, ToastService } from '@libs/shared-services';
import {
  destructureFullSeries,
  FullSeriesComponent,
  ImagingDateLabelComponent,
  ImagingGalleryItem,
  ImagingImageCardComponent,
  ToothPositionLabelComponent,
} from '@libs/imaging';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeFirstMatching } from '@libs/utils';
import {
  CollaborationImageSelectorDialogData,
  GalleryItemTabs,
  ImagingButtonGroupRecord,
} from './collaboration-image-selector-dialog.interface';
import { CollaborationImageSelectorDialogService } from './collaboration-image-selector-dialog.service';

@Component({
  selector: 'lib-collaboration-image-selector-dialog',
  imports: [
    CommonModule,
    TranslateModule,
    ButtonGroupModule,
    SvgIconComponent,
    ImagingImageCardComponent,
    LottieComponent,
    PushTagOnClickDirective,
    FullSeriesComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    ImagingDateLabelComponent,
    ToothPositionLabelComponent,
  ],
  providers: [CollaborationImageSelectorDialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './collaboration-image-selector-dialog.component.html',
  styleUrl: './collaboration-image-selector-dialog.component.scss',
})
export class CollaborationImageSelectorDialogComponent implements OnInit {
  public dialogData: CollaborationImageSelectorDialogData = inject(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly screenService = inject(CollaborationImageSelectorDialogService);
  private readonly destroyRef = inject(DestroyRef);

  @ContentChild('fullSeries') public fullSeriesComponent!: FullSeriesComponent;

  protected navbarItems = Object.values(GalleryItemTabs).filter((tab) => tab !== GalleryItemTabs.Scans);
  protected navbarRecords = ImagingButtonGroupRecord;
  protected GalleryItemTabs = GalleryItemTabs;
  protected TagEvent = TagEvent;

  protected readonly activeTab = computed(() => this.screenService.viewModel().activeTab);
  protected readonly attachments = computed(() => this.screenService.viewModel().selectedImages);
  protected readonly messageAttachments = computed(() => this.screenService.viewModel().attachments);
  protected readonly galleryItems = computed(() => this.screenService.viewModel().galleryItems);
  protected readonly selectedImageIds = computed(() => {
    return [...new Set(this.screenService.viewModel().selectedImages.map((image) => image.id))];
  });
  protected readonly selectedImagesCount = computed(() => this.screenService.viewModel().selectedImages.length);
  protected readonly currentTabImages = computed(() => {
    const galleryItems = this.galleryItems();
    const activeTab = this.activeTab();
    if (galleryItems) {
      return activeTab === GalleryItemTabs.FullSeries
        ? destructureFullSeries(galleryItems[GalleryItemTabs.FullSeries])
        : galleryItems[activeTab] ?? [];
    }

    return [];
  });
  protected readonly currentTabImageIds = computed(() => {
    return this.currentTabImages().map((image) => image.id);
  });
  protected readonly currentTabSelectedImageCount = computed(() => {
    return this.currentTabImageIds().filter((id) => this.selectedImageIds().includes(id)).length;
  });

  public readonly areAttachmentsLoadingSignal = this.screenService.areAttachmentsLoadingSignal;
  public readonly areImagesLoadingSignal = this.screenService.areImagesLoadingSignal;

  protected readonly selectAllImagesControl = new FormControl(false);
  protected readonly mappedImages$ = toObservable(this.messageAttachments);

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  constructor() {
    effect(() => {
      if (this.currentTabSelectedImageCount() === this.currentTabImages().length) {
        this.selectAllImagesControl.setValue(true, { emitEvent: false });
      } else if (this.selectAllImagesControl.value && this.currentTabImages().length > this.currentTabSelectedImageCount()) {
        this.selectAllImagesControl.setValue(false, { emitEvent: false });
      }
    });
  }

  public ngOnInit(): void {
    this.screenService.setPatientAndSiteId({
      patientId: this.dialogData.patientId,
      siteId: this.dialogData.siteId,
    });
    if (this.dialogData.selectedImages) {
      this.screenService.setAttachments(this.dialogData.selectedImages);
    }

    this.selectAllImagesControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        const unselectedCurrentTabImages = this.currentTabImages().filter((image) => !this.selectedImageIds().includes(image.id));
        this.onImageSelectionChanged(unselectedCurrentTabImages, true);
      } else {
        this.onImageSelectionChanged(this.currentTabImages(), false);
      }
    });

    this.screenService.init();
  }

  public onTabSelected(tab: GalleryItemTabs): void {
    this.screenService.setActiveTab(tab);
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public onAddSelections(): void {
    if (this.attachments().length === 0) {
      this.toastService.error(this.translateService.instant('collab_image_selector_modal_no_image_selected'));
      return;
    }

    this.screenService.mapImagesToAttachment();
    this.mappedImages$.pipe(takeFirstMatching((attachments) => !!attachments.data?.length)).subscribe((attachments) => {
      this.dialogRef.close({
        attachments: attachments.data,
      });
    });
  }

  protected onImageSelectionChanged(images: ImagingGalleryItem[], selected: boolean): void {
    if (selected) {
      this.screenService.addAttachment(images);
    } else {
      this.screenService.removeAttachment(images);
    }
  }
}
